import { useCallback, useMemo, useRef, useState } from 'react';
import {
  AnimatedPopover,
  Content,
  Inline,
  InlineItem,
  Icon,
  Menu,
  MenuItem,
  Strong,
  Text,
  useEventListener,
  useOnClickOutside,
} from '@kinesis/bungle';
import { ScenarioIcon } from '@/components/scenario-icon';
import { Scenario } from '@/types';

type ScenarioOptionProps = {
  label: string;
  selected: boolean;
  onClick: () => void;
};

// One row in the scenario popover: a label with a check on the current
// selection — scenarios are flat, no hierarchy indent.
const ScenarioOption = (props: ScenarioOptionProps) => {
  const { label, selected, onClick } = props;
  return (
    <MenuItem
      onClick={onClick}
      content={
        <Inline>
          <InlineItem sizing='fill-container'>
            <Text>{label}</Text>
          </InlineItem>
          {selected && (
            <InlineItem sizing='fit-content'>
              <Icon magnitude='small' type='check' />
            </InlineItem>
          )}
        </Inline>
      }
    />
  );
};

// Scenario-selector editor: a config row showing the pinned scenario's name
// that opens a popover listing the inventory's scenarios (default first).
// Picking one re-renders the surface against that scenario's ledger input
// (EF libraries + scope rules). Shared by the disclosure header and the
// pathway sidebar — a pathway's basis binds a scenario exactly as a
// disclosure pins one. No "clear" entry: both surfaces always render
// against some scenario. Extracted from `disclosure-header` when the
// second consumer landed.
const ScenarioMenuItem = (props: ScenarioMenuItemProps) => {
  const { scenarios, value, onChange } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const close = useCallback(() => setIsOpen(false), []);

  const selected = useMemo(
    () => (value != null ? scenarios.find((s) => s.id === value) : undefined),
    [scenarios, value],
  );

  const pick = useCallback(
    (scenario: number) => {
      onChange(scenario);
      close();
    },
    [onChange, close],
  );

  useOnClickOutside(
    useMemo(() => [ref, popoverRef], []),
    useCallback(() => close(), [close]),
  );

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  });

  return (
    <>
      <MenuItem
        ref={ref}
        icon={<ScenarioIcon />}
        content={
          selected ? (
            <Text>
              <Strong>{selected.name}</Strong> scenario
            </Text>
          ) : (
            <Text tone='secondary'>Select scenario…</Text>
          )
        }
        onClick={toggle}
      />
      <AnimatedPopover
        stopPropagation
        justify='stretch'
        open={isOpen}
        placement='bottom'
        preventOverflow
        targetRef={ref}
      >
        <Content
          ref={popoverRef}
          borderRadiusSize='large'
          border
          shadow
          background='white'
          padding='small'
        >
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            <Menu>
              {scenarios.map((scenario) => (
                <ScenarioOption
                  key={scenario.id}
                  label={scenario.name}
                  selected={scenario.id === value}
                  onClick={() => pick(scenario.id)}
                />
              ))}
            </Menu>
          </div>
        </Content>
      </AnimatedPopover>
    </>
  );
};

export type ScenarioMenuItemProps = {
  scenarios: Scenario[];
  value: number | null;
  onChange: (scenario: number) => void;
};

export { ScenarioMenuItem };
